import type { Call, Hatch, Party, Strength } from "./types";

/* ── Palette ───────────────────────────────────────
 * Diverging blue ↔ red with a neutral midpoint (toss-up). Each party ramp
 * uses three steps matched in lightness across parties so "likely D" and
 * "likely R" read as equally strong. Independents get an amber ramp
 * (violet collapses into blue for deuteranopes). Ramps were checked with
 * the dataviz palette validator: monotone lightness, light end ≥ 2:1.
 * Map fills are SVG paint, so they are explicit colors rather than theme
 * tokens (same approach as the launch simulator map).
 */

type Lead = Exclude<Strength, "tossup">;

export const RAMPS: Record<Party, Record<Lead, string>> = {
  D: { lean: "#86b6ef", likely: "#3987e5", safe: "#184f95" },
  I: { lean: "#eda100", likely: "#c98500", safe: "#8f5f00" },
  R: { lean: "#f08c8a", likely: "#e34948", safe: "#9b2221" },
};

export const PARTY_COLOR: Record<Party, string> = {
  D: RAMPS.D.likely,
  I: RAMPS.I.lean,
  R: RAMPS.R.likely,
};

export const TOSSUP = "#bab5ab";
/** States with no race for the office: recede into the background. */
export const NO_RACE = "#efeeeb";
/** A race exists but the layer has no reading yet. */
export const NO_DATA = "#ffffff";
export const NO_DATA_STROKE = "#b8b5ae";
export const STRIPE = "rgba(255, 255, 255, 0.75)";

export const STRENGTH_LABEL: Record<Strength, string> = {
  lean: "Lean",
  likely: "Likely",
  safe: "Safe",
  tossup: "Toss-up",
};

export const fillFor = (call: Call): string => {
  if (call.leftProbability === null) return NO_DATA;
  if (call.strength === "tossup" || call.leader === null) return TOSSUP;
  return RAMPS[call.leader][call.strength];
};

/** Bucket a margin (points) with explicit thresholds, e.g. for counties. */
export const strengthForMargin = (
  margin: number,
  thresholds: [number, number, number]
): Strength => {
  const size = Math.abs(margin);
  if (size < thresholds[0]) return "tossup";
  if (size < thresholds[1]) return "lean";
  if (size < thresholds[2]) return "likely";
  return "safe";
};

export const fillForMargin = (
  margin: number,
  thresholds: [number, number, number],
  leftParty: Party = "D"
): string => {
  const strength = strengthForMargin(margin, thresholds);
  if (strength === "tossup") return TOSSUP;
  return RAMPS[margin > 0 ? leftParty : "R"][strength];
};

/** County thresholds: counties are far more lopsided than states. */
export const COUNTY_THRESHOLDS: [number, number, number] = [4, 15, 30];
/** Swing vs 2024, in points (two steps per side, matching the legend). */
export const SHIFT_THRESHOLDS: [number, number, number] = [2, 6, Infinity];

/* ── Stripes for projected flips ───────────────── */

/**
 * A projected flip keeps its fill and gains 45° stripes: white over a
 * party shade, or the leader's color over a toss-up grey.
 */
export const hatchFor = (call: Call): Hatch => {
  const base = fillFor(call);
  const stripe =
    base === TOSSUP && call.leader ? RAMPS[call.leader].likely : STRIPE;
  return {
    base,
    id: `flip-${base.replace("#", "")}-${stripe.replace(/[^a-z0-9]/gi, "")}`,
    stripe,
  };
};

export const formatMargin = (
  margin: number | null,
  leftParty: Party = "D",
  rightParty: Party = "R"
): string => {
  if (margin === null) return "—";
  if (Math.abs(margin) < 0.05) return "Even";
  return `${margin > 0 ? leftParty : rightParty}+${Math.abs(margin).toFixed(1)}`;
};

export const formatPercent = (share: number | null, digits = 0): string =>
  share === null ? "—" : `${(share * 100).toFixed(digits)}%`;

/** "Oct 30, 10:42 AM". */
export const formatTime = (iso: string): string =>
  new Date(iso).toLocaleString("en-US", {
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
    month: "short",
  });
